import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import { OnboardingState } from "./types";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// Check if we're in the Lovable editor or if admin mode is enabled via URL
export function isLovableEditor(): boolean {
  return window.location.hostname === 'lovable.dev' || 
         window.location.hostname.includes('localhost') ||
         window.location.hostname.includes('preview.lovable.app');
}

// Check if admin mode is enabled via URL parameter
export function isAdminMode(): boolean {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get('admin-mode') === 'true' || 
         isLovableEditor() || 
         false;
}

// Format time remaining for timer display
export function formatTimeRemaining(seconds: number): string {
  // For timers over 1 hour, show hh:mm format
  if (seconds >= 3600) {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${hours}h ${minutes}m`;
  }
  
  // For timers under 1 hour but over 10 minutes, show mm:ss format
  if (seconds >= 600) {
    const minutes = Math.floor(seconds / 60);
    return `${minutes} minutes`;
  }
  
  // For shorter timers, use traditional mm:ss format
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.floor(seconds % 60);
  return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
}

// Validate email
export function validateEmail(email: string): boolean {
  const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return re.test(email);
}

// Validate phone number (simple validation)
export function validatePhone(phone: string): boolean {
  // This is a simple validation that just checks for digits and some special characters
  const re = /^[+]?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,6}$/;
  return re.test(phone);
}

// Check if all todos in a list are completed
export function areAllTodosCompleted(
  todos: { id: string }[] | undefined, 
  completedTodos: Record<string, string[]>,
  stepId: string
): boolean {
  if (!todos || todos.length === 0) return true;
  const completedInStep = completedTodos[stepId] || [];
  return todos.every(todo => completedInStep.includes(todo.id));
}

// Debounce function to limit frequency of function calls
export function debounce<T extends (...args: any[]) => any>(
  func: T, 
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: number | null = null;
  
  return function(...args: Parameters<T>) {
    if (timeout) {
      clearTimeout(timeout);
    }
    
    timeout = window.setTimeout(() => {
      func(...args);
      timeout = null;
    }, wait);
  };
}

// Simple deep comparison of objects for onboarding state
export function isEqual(obj1: any, obj2: any): boolean {
  if (obj1 === obj2) return true;
  
  if (typeof obj1 !== 'object' || typeof obj2 !== 'object' || obj1 === null || obj2 === null) {
    return obj1 === obj2;
  }
  
  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);
  
  if (keys1.length !== keys2.length) return false;
  
  for (const key of keys1) {
    if (!keys2.includes(key)) return false;
    if (!isEqual(obj1[key], obj2[key])) return false;
  }
  
  return true;
}
